import { useQuery } from '@tanstack/react-query';
import { Building2, ChevronsUpDown, Check, X } from 'lucide-react';
import { useState, useCallback, useRef, useEffect } from 'react';
import { searchCustomers, fetchCustomerById, type Customer } from '@/core/api';
import { useFactory } from '@/core/hooks/useFactory';
import { useTenant } from '@/core/hooks/useTenant';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/shared/ui/command';
import { Button } from '@/shared/ui/button';
import { Badge } from '@/shared/ui/badge';
import { cn } from '@/shared/lib/utils';

interface CustomerSelectorProps {
  value: string | null;
  onValueChange: (value: string | null) => void;
  allowClear?: boolean;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

/**
 * Efficient customer selector optimized for large datasets (100k+ records).
 * - Server-side search only (no upfront data loading)
 * - Debounced search input
 * - Fetches selected item separately for display
 */
export const CustomerSelector = ({
  value,
  onValueChange,
  allowClear = false,
  placeholder = 'Select customer',
  disabled = false,
  className,
}: CustomerSelectorProps) => {
  const { selectedFactoryId } = useFactory();
  const { selectedTenantId } = useTenant();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceRef = useRef<NodeJS.Timeout>();

  // Debounce search input
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(search);
    }, 200);
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [search]);

  // Fetch selected customer for display (only when we have a value)
  const { data: selectedCustomer } = useQuery({
    queryKey: ['customer', value],
    queryFn: () => {
      if (!value) return null;
      return fetchCustomerById(value);
    },
    enabled: !!value,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Server-side search - only when user types
  const { data: searchResults = [], isFetching } = useQuery({
    queryKey: ['customers-search', selectedTenantId, selectedFactoryId, debouncedSearch],
    queryFn: () => {
      if (!selectedFactoryId || debouncedSearch.length < 1) return [];
      return searchCustomers(selectedFactoryId, debouncedSearch);
    },
    enabled: !!selectedFactoryId && debouncedSearch.length >= 1,
    staleTime: 30 * 1000, // Cache search results for 30 seconds
  });

  const handleSelect = useCallback((customerId: string) => {
    onValueChange(customerId);
    setOpen(false);
    setSearch('');
  }, [onValueChange]);

  const handleClear = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onValueChange(null);
  }, [onValueChange]);

  const formatCustomerDisplay = (customer: Customer) => {
    if (customer.internal_code) {
      return `${customer.name} (${customer.internal_code})`;
    }
    return customer.name;
  };

  if (!selectedFactoryId) {
    return (
      <Badge variant="secondary" className="text-xs">
        Select factory first
      </Badge>
    );
  }

  return (
    <div className={className}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className={cn(
              "w-full justify-between font-normal",
              !value && "text-muted-foreground"
            )}
          >
            <div className="flex items-center gap-2 min-w-0">
              <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="truncate">
                {selectedCustomer ? formatCustomerDisplay(selectedCustomer) : placeholder}
              </span>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {allowClear && value && (
                <X
                  className="h-4 w-4 opacity-50 hover:opacity-100"
                  onClick={handleClear}
                />
              )}
              <ChevronsUpDown className="h-4 w-4 opacity-50" />
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search by name or code..."
              value={search}
              onValueChange={setSearch}
            />
            <CommandList>
              {debouncedSearch.length === 0 && (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  Type to search customers...
                </div>
              )}
              {debouncedSearch.length > 0 && searchResults.length === 0 && !isFetching && (
                <CommandEmpty>No customers found.</CommandEmpty>
              )}
              {isFetching && debouncedSearch.length > 0 && (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  Searching...
                </div>
              )}
              {searchResults.length > 0 && (
                <CommandGroup>
                  {searchResults.map((customer) => (
                    <CommandItem
                      key={customer.id}
                      value={customer.id}
                      onSelect={handleSelect}
                      className="cursor-pointer"
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4 shrink-0",
                          value === customer.id ? "opacity-100" : "opacity-0"
                        )}
                      />
                      <div className="flex flex-col min-w-0">
                        <span className="truncate">{formatCustomerDisplay(customer)}</span>
                        {customer.description && (
                          <span className="text-xs text-muted-foreground truncate">
                            {customer.description}
                          </span>
                        )}
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};
